import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl } from '@/config/api';
import { useAuth } from '../context/AuthContext.jsx';
import { useTranslation } from 'react-i18next';
import {
  getProfileTranslations,
  normalizeProfileLanguage,
  profileLanguages,
} from '@/utils/profileTranslations.js';

const defaultSocialLinks = {
  website: '',
  instagram: '',
  facebook: '',
  telegram: '',
  youtube: '',
};

const buildDefaultLocalizedSocialLinks = () =>
  profileLanguages.reduce((acc, lang) => {
    acc[lang] = { ...defaultSocialLinks };
    return acc;
  }, {});

const buildLocalizedSocialLinks = (profile) => {
  const translations = profile?.socialLinksTranslations;
  const rawSocialLinks = profile?.socialLinks || {};
  const hasLocalizedStructure = profileLanguages.some((lang) => rawSocialLinks?.[lang]);

  return profileLanguages.reduce((acc, lang) => {
    const localized = translations?.[lang]
      || (hasLocalizedStructure ? rawSocialLinks?.[lang] : rawSocialLinks)
      || rawSocialLinks?.[`${lang}SocialLinks`];

    acc[lang] = { ...defaultSocialLinks, ...(localized || {}) };
    return acc;
  }, buildDefaultLocalizedSocialLinks());
};

const buildFormState = (profile) => ({
  name: getProfileTranslations(profile, 'name'),
  phone: profile?.phone || '',
  gameLanguage: getProfileTranslations(profile, 'gameLanguage', { fillMissingWithBase: true }),
  shortInfo: getProfileTranslations(profile, 'shortInfo'),
  bio: getProfileTranslations(profile, 'bio'),
  avatarUrl: profile?.avatarUrl || '',
  socialLinks: buildLocalizedSocialLinks(profile),
});

const resolveAvatarPreview = (value) => {
  if (!value) {
    return '';
  }
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) {
    return value;
  }
  return apiUrl(value);
};

const parseUploadError = async (response, translate) => {
  try {
    const data = await response.json();
    if (data?.error) {
      return data.error;
    }
  } catch (error) {
    // ignore
  }
  return translate('messages.uploadFailed');
};

const parseModerationError = async (response, translate) => {
  try {
    const data = await response.json();
    if (data?.errorCode) {
      if (data.errorCode === 'CONDUCTOR_APPLICATION_PENDING') {
        return translate('moderation.messages.pendingDetailed');
      }
      if (data.errorCode === 'CONDUCTOR_APPLICATION_COOLDOWN') {
        return translate('moderation.messages.cooldownDetailed');
      }
    }
    if (data?.error) {
      return data.error;
    }
  } catch (error) {
    // ignore
  }
  return translate('moderation.messages.submitFailed');
};

const PENDING_MODERATION_STATUSES = new Set(['new', 'pending', 'inReview']);
const FINAL_MODERATION_STATUSES = new Set(['approved', 'rejected']);
const MODERATION_COOLDOWN_DAYS = 14;

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const resolveModerationSource = (user) =>
  user?.conductorModeration
    || user?.moderationStatus
    || user?.conductorApplication
    || user?.lastConductorApplication
    || user?.conductorApplicationStatus
    || null;

const EMPTY_MODERATION_ENTRY = {
  status: null,
  lastSubmittedAt: null,
  nextAvailableAt: null,
  decidedAt: null,
};

const buildEmptyModerationMap = () =>
  profileLanguages.reduce((acc, lang) => {
    acc[lang] = { ...EMPTY_MODERATION_ENTRY };
    return acc;
  }, {});

const extractSingleModerationState = (user, previousState = null) => {
  const baseState = previousState || { ...EMPTY_MODERATION_ENTRY };
  const source = resolveModerationSource(user);
  if (!source) {
    return baseState;
  }

  const normalized = {
    status: source.status || source.lastStatus || source.state || baseState.status,
    lastSubmittedAt:
      source.lastSubmittedAt
      || source.submittedAt
      || source.createdAt
      || baseState.lastSubmittedAt,
    nextAvailableAt: source.nextAvailableAt || source.availableAt || source.nextSubmitAt || baseState.nextAvailableAt,
    decidedAt: source.decidedAt || source.resolvedAt || source.finishedAt || baseState.decidedAt,
  };

  return normalized;
};

const buildModerationMapFromUser = (user, previousState = null) => {
  if (!user) {
    return buildEmptyModerationMap();
  }
  const base = buildEmptyModerationMap();
  const fallback = extractSingleModerationState(user, previousState?.ua);
  if (!fallback.status && !fallback.lastSubmittedAt) {
    return previousState || base;
  }

  return profileLanguages.reduce((acc, lang) => {
    acc[lang] = { ...fallback };
    return acc;
  }, base);
};

const normalizeModerationEntry = (entry = {}) => ({
  status: entry.status || entry.lastStatus || entry.state || null,
  lastSubmittedAt: entry.lastSubmittedAt || entry.submittedAt || entry.createdAt || null,
  nextAvailableAt: entry.nextAvailableAt || entry.availableAt || entry.nextSubmitAt || null,
  decidedAt: entry.decidedAt || entry.resolvedAt || entry.finishedAt || null,
  language: entry.language || entry.submissionLanguage || entry.locale || null,
});

const mergeModerationState = (previous = null, updates = null, fallback = null) => {
  const base = previous || buildEmptyModerationMap();
  const merged = { ...base };
  profileLanguages.forEach((lang) => {
    merged[lang] = { ...EMPTY_MODERATION_ENTRY, ...(base?.[lang] || {}) };
  });

  if (updates) {
    profileLanguages.forEach((lang) => {
      if (updates[lang]) {
        merged[lang] = { ...merged[lang], ...updates[lang] };
      }
    });
  }

  if (fallback) {
    profileLanguages.forEach((lang) => {
      const current = merged[lang];
      if (!current.status && !current.lastSubmittedAt && fallback[lang]) {
        merged[lang] = { ...current, ...fallback[lang] };
      }
    });
  }

  return merged;
};

const parseModerationStatuses = (payload, previousState = null, fallbackState = null) => {
  const updates = {};

  const applyEntry = (entry) => {
    const normalized = normalizeModerationEntry(entry);
    const language = normalized.language ? normalizeProfileLanguage(normalized.language) : 'ua';
    updates[language] = {
      status: normalized.status,
      lastSubmittedAt: normalized.lastSubmittedAt,
      nextAvailableAt: normalized.nextAvailableAt,
      decidedAt: normalized.decidedAt,
    };
  };

  if (payload?.byLanguage && typeof payload.byLanguage === 'object') {
    Object.entries(payload.byLanguage).forEach(([language, entry]) => {
      applyEntry({ ...entry, language });
    });
  }

  const list = Array.isArray(payload?.applications)
    ? payload.applications
    : Array.isArray(payload?.statuses)
      ? payload.statuses
      : null;

  if (list) {
    list.forEach(applyEntry);
  }

  return mergeModerationState(previousState, updates, fallbackState);
};

const addDays = (date, days) => new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

const optionalString = (value) => {
  const trimmed = value?.trim?.();
  return trimmed ? trimmed : null;
};

const normalizeSocialLinksForLanguage = (socialLinks = {}) =>
  Object.keys(defaultSocialLinks).reduce((acc, key) => {
    acc[key] = (socialLinks?.[key] || '').trim();
    return acc;
  }, { ...defaultSocialLinks });

const normalizeSocialLinksForPayload = (socialLinks = {}) =>
  profileLanguages.reduce((acc, language) => {
    acc[language] = Object.fromEntries(
      Object.keys(defaultSocialLinks).map((key) => [key, optionalString(socialLinks?.[language]?.[key])]),
    );
    return acc;
  }, {});

const getProfileValidationErrors = (form) => {
  const normalizedLanguage = 'ua';
  const hasSocialLink = Object.values(form?.socialLinks?.[normalizedLanguage] || {}).some((value) =>
    Boolean(value?.trim?.()),
  );

  return {
    name: !form?.name?.[normalizedLanguage]?.trim?.(),
    phone: !form?.phone?.trim?.(),
    gameLanguage: !form?.gameLanguage?.[normalizedLanguage]?.trim?.(),
    shortInfo: !form?.shortInfo?.[normalizedLanguage]?.trim?.(),
    avatarUrl: !form?.avatarUrl?.trim?.(),
    socialLinks: !hasSocialLink,
  };
};

const hasProfileValidationErrors = (errors) => Object.values(errors).some(Boolean);

const normalizeFormForCompare = (form) => ({
  name: profileLanguages.reduce((acc, lang) => {
    acc[lang] = (form?.name?.[lang] || '').trim();
    return acc;
  }, {}),
  phone: (form?.phone || '').trim(),
  gameLanguage: profileLanguages.reduce((acc, lang) => {
    acc[lang] = (form?.gameLanguage?.[lang] || '').trim();
    return acc;
  }, {}),
  shortInfo: profileLanguages.reduce((acc, lang) => {
    acc[lang] = (form?.shortInfo?.[lang] || '').trim();
    return acc;
  }, {}),
  bio: profileLanguages.reduce((acc, lang) => {
    acc[lang] = (form?.bio?.[lang] || '').trim();
    return acc;
  }, {}),
  avatarUrl: (form?.avatarUrl || '').trim(),
  socialLinks: profileLanguages.reduce((acc, lang) => {
    acc[lang] = normalizeSocialLinksForLanguage(form?.socialLinks?.[lang]);
    return acc;
  }, {}),
});

const formatDateByLocale = (value, locale) => {
  const date = normalizeDate(value);
  if (!date) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(locale || 'ua', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch (error) {
    return date.toLocaleDateString();
  }
};

const ProfileSettingsPage = () => {
  const { t, i18n } = useTranslation('profile');
  const { user, token, initializing, updateProfile } = useAuth();
  const [form, setForm] = useState(buildFormState(null));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const avatarInputRef = useRef(null);
  const activeLanguage = 'ua';
  const [moderationState, setModerationState] = useState(() => buildModerationMapFromUser(user));
  const [moderationPending, setModerationPending] = useState(false);
  const [moderationLoading, setModerationLoading] = useState(false);
  const [moderationModalOpen, setModerationModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (user) {
      setForm(buildFormState(user));
      setAvatarPreview(resolveAvatarPreview(user.avatarUrl || ''));
    } else {
      setForm(buildFormState(null));
      setAvatarPreview('');
    }
  }, [user]);

  useEffect(() => {
    setModerationState((prev) => buildModerationMapFromUser(user, prev));
  }, [user]);

  const savedFormState = useMemo(() => buildFormState(user), [user]);
  const hasAvatar = Boolean(avatarPreview);

  const fetchModerationStatuses = useCallback(async () => {
    if (!user || !token) {
      setModerationState(buildModerationMapFromUser(user));
      return;
    }

    setModerationLoading(true);

    try {
      const response = await fetch(apiUrl('/api/conductor-applications/statuses'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await parseModerationError(response, t));
      }

      const data = await response.json();
      setModerationState((prev) => parseModerationStatuses(data, prev, buildModerationMapFromUser(user, prev)));
    } catch (statusError) {
      setNotification({
        title: t('messages.errorTitle'),
        message: statusError.message || t('moderation.messages.submitFailed'),
      });
      setModerationState((prev) => mergeModerationState(prev, null, buildModerationMapFromUser(user, prev)));
    } finally {
      setModerationLoading(false);
    }
  }, [t, token, user]);

  useEffect(() => {
    fetchModerationStatuses();
  }, [fetchModerationStatuses]);

  const hasUnsavedChanges = useMemo(() => {
    const normalizedForm = normalizeFormForCompare(form);
    const normalizedSaved = normalizeFormForCompare(savedFormState);
    return JSON.stringify(normalizedForm) !== JSON.stringify(normalizedSaved);
  }, [form, savedFormState]);

  const profileValidationErrors = useMemo(() => getProfileValidationErrors(form), [form]);
  const isProfileValid = useMemo(
    () => !hasProfileValidationErrors(profileValidationErrors),
    [profileValidationErrors],
  );

  const activeModeration = moderationState[activeLanguage] || { ...EMPTY_MODERATION_ENTRY };

  const lastSubmittedDate = useMemo(
    () => normalizeDate(activeModeration.lastSubmittedAt),
    [activeModeration.lastSubmittedAt],
  );

  const nextAvailableDate = useMemo(() => {
    const directDate = normalizeDate(activeModeration.nextAvailableAt);
    if (directDate) {
      return directDate;
    }

    return lastSubmittedDate ? addDays(lastSubmittedDate, MODERATION_COOLDOWN_DAYS) : null;
  }, [activeModeration.nextAvailableAt, lastSubmittedDate]);

  const cooldownActive = useMemo(() => {
    if (!nextAvailableDate) {
      return false;
    }
    const now = Date.now();
    return nextAvailableDate.getTime() > now;
  }, [nextAvailableDate]);

  const cooldownDateLabel = useMemo(
    () => formatDateByLocale(nextAvailableDate, i18n.language),
    [i18n.language, nextAvailableDate],
  );

  const pendingModeration = PENDING_MODERATION_STATUSES.has(activeModeration.status);
  const finalModeration = FINAL_MODERATION_STATUSES.has(activeModeration.status);

  const profileUpdatedAfterSubmission = useMemo(() => {
    if (!finalModeration || !lastSubmittedDate || !user?.updatedAt) {
      return false;
    }
    const updatedAt = normalizeDate(user.updatedAt);
    return updatedAt ? updatedAt > lastSubmittedDate : false;
  }, [finalModeration, lastSubmittedDate, user?.updatedAt]);

  const moderationTitle = finalModeration && profileUpdatedAfterSubmission
    ? t('moderation.title.updated')
    : t('moderation.title.initial');

  const isLanguageBlockValid = useCallback(() => !hasProfileValidationErrors(profileValidationErrors), [profileValidationErrors]);

  const moderationDisabledReason = useMemo(() => {
    if (hasUnsavedChanges) {
      return t('moderation.hints.unsaved');
    }
    if (!isProfileValid) {
      return t('moderation.hints.invalid');
    }
    if (!isLanguageBlockValid()) {
      return t('moderation.hints.invalid');
    }
    if (moderationLoading) {
      return t('moderation.hints.loading');
    }
    if (pendingModeration) {
      return t('moderation.hints.pending');
    }
    if (cooldownActive && cooldownDateLabel) {
      return t('moderation.hints.cooldown', { date: cooldownDateLabel });
    }
    return '';
  }, [
    cooldownActive,
    cooldownDateLabel,
    hasUnsavedChanges,
    isLanguageBlockValid,
    isProfileValid,
    moderationLoading,
    pendingModeration,
    t,
  ]);

  const showSentState = !hasUnsavedChanges && isProfileValid && (pendingModeration || cooldownActive);

  const moderationButtonLabel = moderationPending
    ? t('moderation.button.sending')
    : showSentState
      ? t('moderation.button.sent')
      : t('moderation.button.send');

  const moderationButtonDisabled = Boolean(moderationDisabledReason) || moderationPending || moderationLoading;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleLocalizedChange = (field, language) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [language]: value,
      },
    }));
    setValidationErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleLocalizedSocialChange = (language, name) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [language]: {
          ...prev.socialLinks?.[language],
          [name]: value,
        },
      },
    }));
    setValidationErrors((prev) => ({ ...prev, socialLinks: false }));
  };

  const normalizeTranslationsForPayload = (values) =>
    Object.fromEntries(profileLanguages.map((language) => [language, optionalString(values?.[language])]));

  const buildModerationProfilePayload = (language) => {
    const normalizedLanguage = normalizeProfileLanguage(language);
    const name = optionalString(form.name?.[normalizedLanguage]);
    const shortInfo = optionalString(form.shortInfo?.[normalizedLanguage]);
    const gameLanguageValue = optionalString(form.gameLanguage?.[normalizedLanguage]);
    const phoneValue = form.phone.trim();
    const avatarUrlValue = optionalString(form.avatarUrl);
    const hasSocialLink = Object.values(form.socialLinks?.[normalizedLanguage] || {}).some((value) =>
      Boolean(value?.trim?.()),
    );

    if (!name || !shortInfo || !gameLanguageValue || !phoneValue || !avatarUrlValue || !hasSocialLink) {
      return null;
    }

    return {
      language: normalizedLanguage,
      name,
      shortInfo,
      gameLanguage: gameLanguageValue,
      phone: phoneValue,
      avatarUrl: avatarUrlValue,
      socialLinks: Object.fromEntries(
        Object.keys(defaultSocialLinks).map((key) => [key, optionalString(form.socialLinks?.[normalizedLanguage]?.[key])]),
      ),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const nameUa = form.name?.ua?.trim?.() || '';
    const phoneValue = form.phone.trim();
    const nextValidationErrors = getProfileValidationErrors(form);
    setValidationErrors(nextValidationErrors);

    const socialLinksPayload = normalizeSocialLinksForPayload(form.socialLinks);
    const payload = {
      name: nameUa,
      phone: phoneValue,
      gameLanguage: optionalString(form.gameLanguage?.ua),
      gameLanguageTranslations: normalizeTranslationsForPayload(form.gameLanguage),
      shortInfo: optionalString(form.shortInfo?.ua),
      avatarUrl: optionalString(form.avatarUrl),
      socialLinks: socialLinksPayload,
      socialLinksTranslations: socialLinksPayload,
      nameTranslations: normalizeTranslationsForPayload(form.name),
      shortInfoTranslations: normalizeTranslationsForPayload(form.shortInfo),
    };

    if (hasProfileValidationErrors(nextValidationErrors)) {
      setError(t('messages.requiredFields'));
      return;
    }

    try {
      setPending(true);
      await updateProfile(payload);
      setSuccess(t('messages.saved'));
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setPending(false);
    }
  };

  const handleModerationSubmit = async () => {
    if (!token) {
      setNotification({ title: t('messages.errorTitle'), message: t('messages.authRequired') });
      return;
    }

    const nextValidationErrors = getProfileValidationErrors(form);
    if (hasProfileValidationErrors(nextValidationErrors)) {
      setValidationErrors(nextValidationErrors);
      setNotification({ title: t('messages.errorTitle'), message: t('messages.requiredFields') });
      return;
    }

    const profilePayload = buildModerationProfilePayload('ua');

    if (!profilePayload) {
      setNotification({
        title: t('messages.errorTitle'),
        message: t('moderation.messages.languageInvalid'),
      });
      return;
    }

    try {
      setModerationPending(true);
      const submissionLanguage = 'ua';
      const response = await fetch(apiUrl('/api/conductor-applications'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          language: submissionLanguage,
          submissionLanguage,
          profile: profilePayload,
          uiLanguage: normalizeProfileLanguage(i18n.language),
          email: user?.email || '',
        }),
      });

      if (!response.ok) {
        throw new Error(await parseModerationError(response, t));
      }

      const data = await response.json();
      setModerationState((prev) =>
        mergeModerationState(prev, {
          [submissionLanguage]: {
            status: data.status || 'new',
            lastSubmittedAt: data.submittedAt || data.createdAt || new Date().toISOString(),
            nextAvailableAt: data.nextAvailableAt || data.availableAt || null,
            decidedAt: data.decidedAt || null,
          },
        }),
      );
      setModerationModalOpen(true);
    } catch (submitError) {
      setNotification({
        title: t('messages.errorTitle'),
        message: submitError.message || t('moderation.messages.submitFailed'),
      });
    } finally {
      setModerationPending(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    setUploadError('');
    if (!token) {
      setUploadError(t('messages.authRequired'));
      input.value = '';
      return;
    }
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(apiUrl('/api/upload/avatar'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(await parseUploadError(response, t));
      }
      const data = await response.json();
      setForm((prev) => ({ ...prev, avatarUrl: data.url }));
      setValidationErrors((prev) => ({ ...prev, avatarUrl: false }));
      setAvatarPreview(resolveAvatarPreview(data.url));
      setSuccess(t('messages.avatarSaved'));
    } catch (uploadErr) {
      setUploadError(uploadErr.message || t('messages.uploadFailed'));
    } finally {
      setUploadingAvatar(false);
      input.value = '';
    }
  };

  if (initializing && !user) {
    return (
      <div className="page-card">
        <p className="page-description">{t('states.loading')}</p>
      </div>
    );
  }

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{t('heading.settings')}</p>
          <h1>{t('heading.title')}</h1>
          <p className="page-description">{t('heading.description')}</p>
        </div>
      </div>
      <form className="game-form profile-form" onSubmit={handleSubmit}>
        <section className="profile-section">
          <h2>{t('mainInfo.title')}</h2>
          <div className="form-grid phone-grid">
            <label className="form-field phone-field">
              <span>{t('mainInfo.nameLabel')}</span>
              <input
                type="text"
                name="name-ua"
                value={form.name?.ua || ''}
                onChange={handleLocalizedChange('name', 'ua')}
                placeholder={t('mainInfo.namePlaceholder')}
                required
              />
              {validationErrors.name && <p className="form-error">{t('messages.requiredField')}</p>}
            </label>
            <label className="form-field phone-field">
              <span>{t('mainInfo.phoneLabel')}</span>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t('mainInfo.phonePlaceholder')}
                required
              />
              {validationErrors.phone && <p className="form-error">{t('messages.requiredField')}</p>}
            </label>
            <label className="form-field phone-field">
              <span>{t('mainInfo.emailLabel')}</span>
              <input
                className="profile-readonly-input"
                type="email"
                value={user?.email || ''}
                readOnly
                aria-readonly="true"
              />
            </label>
          </div>
        </section>

        <section className="profile-section">
          <div>
            <h2>{t('localized.title')}</h2>
            <p className="profile-hint">
              {t('localized.hintPrefix')}{' '}
              <a
                href="https://leelaspace.com/leela-online-platform"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('localized.hintLinkText')}
              </a>
              .
            </p>
          </div>
          <div className="localized-block">
            <div className="form-grid">
              <label className="form-field">
                <span>{t('localized.gameLanguageLabel')}</span>
                <input
                  type="text"
                  name="gameLanguage-ua"
                  value={form.gameLanguage?.ua || ''}
                  onChange={handleLocalizedChange('gameLanguage', 'ua')}
                  placeholder={t('mainInfo.gameLanguagePlaceholder')}
                />
                {validationErrors.gameLanguage && <p className="form-error">{t('messages.requiredField')}</p>}
              </label>
            </div>
            <label className="form-field">
              <span>{t('localized.shortInfoLabel')}</span>
              <textarea
                name="shortInfo-ua"
                value={form.shortInfo?.ua || ''}
                onChange={handleLocalizedChange('shortInfo', 'ua')}
                placeholder={t('mainInfo.shortInfoPlaceholder')}
                rows={3}
              />
              {validationErrors.shortInfo && <p className="form-error">{t('messages.requiredField')}</p>}
            </label>

            <div className="profile-subsection">
              <h3>{t('social.title')}</h3>
              <p className="profile-hint">{t('social.hint')}</p>
              <div className="form-grid">
                    <label className="form-field">
                      <span>{t('social.tiktok')}</span>
                      <input
                        type="url"
                        name="website-ua"
                        value={form.socialLinks?.ua?.website || ''}
                        onChange={handleLocalizedSocialChange('ua', 'website')}
                        placeholder={t('social.tiktokPlaceholder')}
                      />
                    </label>
                    <label className="form-field">
                      <span>{t('social.instagram')}</span>
                      <input
                        type="url"
                        name="instagram-ua"
                        value={form.socialLinks?.ua?.instagram || ''}
                        onChange={handleLocalizedSocialChange('ua', 'instagram')}
                        placeholder={t('social.instagramPlaceholder')}
                      />
                    </label>
                    <label className="form-field">
                      <span>{t('social.facebook')}</span>
                      <input
                        type="url"
                        name="facebook-ua"
                        value={form.socialLinks?.ua?.facebook || ''}
                        onChange={handleLocalizedSocialChange('ua', 'facebook')}
                        placeholder={t('social.facebookPlaceholder')}
                      />
                    </label>
                    <label className="form-field">
                      <span>{t('social.telegram')}</span>
                      <input
                        type="url"
                        name="telegram-ua"
                        value={form.socialLinks?.ua?.telegram || ''}
                        onChange={handleLocalizedSocialChange('ua', 'telegram')}
                        placeholder={t('social.telegramPlaceholder')}
                      />
                    </label>
                    <label className="form-field">
                      <span>{t('social.youtube')}</span>
                      <input
                        type="url"
                        name="youtube-ua"
                        value={form.socialLinks?.ua?.youtube || ''}
                        onChange={handleLocalizedSocialChange('ua', 'youtube')}
                        placeholder={t('social.youtubePlaceholder')}
                      />
                    </label>
              </div>
              {validationErrors.socialLinks && <p className="form-error">{t('messages.socialRequired')}</p>}
            </div>
          </div>
        </section>

        <section className="profile-section">
          <h2>{t('photo.title')}</h2>
          <p className="profile-hint">{t('photo.hint')}</p>
          <div className="avatar-upload">
            {avatarPreview ? (
              <img src={avatarPreview} alt={t('photo.alt')} className="avatar-preview" />
            ) : (
              <div className="avatar-placeholder">{t('photo.empty')}</div>
            )}
            <div className="avatar-upload-actions">
              <input
                ref={avatarInputRef}
                className="visually-hidden"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                disabled={uploadingAvatar}
              />
              <button
                type="button"
                className="primary-button avatar-upload-button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {t(hasAvatar ? 'photo.replace' : 'photo.choose')}
              </button>
              {uploadingAvatar && <p className="profile-hint">{t('photo.uploading')}</p>}
              {uploadError && <p className="form-error">{uploadError}</p>}
              {validationErrors.avatarUrl && <p className="form-error">{t('messages.photoRequired')}</p>}
            </div>
          </div>
        </section>

        {error && <p className="form-error">{error}</p>}
        {success && <p className="form-success">{success}</p>}
        <button className="primary-button" type="submit" disabled={pending || uploadingAvatar}>
          {pending ? t('buttons.saving') : t('buttons.save')}
        </button>

        {['conductor', 'super'].includes(user?.role) && (
          <section className="profile-section moderation-section">
            <p className="moderation-caption">
              {moderationTitle}{' '}
              <span className="moderation-note">{t('moderation.note')}</span>
            </p>
            <div className="moderation-actions">
              <button
                type="button"
                className={`primary-button moderation-button${showSentState ? ' outlined success' : ''}`}
                onClick={handleModerationSubmit}
                disabled={moderationButtonDisabled}
              >
                {showSentState && !moderationPending && <span className="moderation-check">✓</span>}
                {moderationButtonLabel}
              </button>
              {moderationDisabledReason && (
                <p className="profile-hint moderation-hint">{moderationDisabledReason}</p>
              )}
            </div>
          </section>
        )}
      </form>

      {moderationModalOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{t('moderation.modal.title')}</h3>
            </div>
            <p className="modal-description">
              {t('moderation.messages.success', { email: user?.email || '' })
                .split('\n')
                .map((line, index, lines) => (
                  <span key={line || index} className="modal-description-line">
                    {line}
                    {index < lines.length - 1 && <br />}
                  </span>
                ))}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => setModerationModalOpen(false)}
              >
                {t('moderation.modal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{notification.title || t('messages.errorTitle')}</h3>
            </div>
            <p className="modal-description">{notification.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => setNotification(null)}
              >
                {t('messages.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettingsPage;
